(ns io.daskul.webapp.api.core
  "Frontend HTTP config + policy core — the single source of truth shared by
   all three HTTP idioms (see ADR F-054).

   Deliberately *ajax-free* so the pure request-base assembly (uri + headers)
   is unit-testable under the :node-test harness without dragging cljs-ajax in.
   The JSON request/response formats (ajax objects) are layered on by the
   ajax-using callers (aggregates.events/build-request and api.http/request!).

   Holds:
   - config        — atom populated by webapp/configure!
   - configure!    — merge config
   - get-api-base / get-auth-header — config accessors
   - handle-http-error — the 401/error policy (e.g. 401 -> logout)
   - base-opts     — pure {:uri :headers} for a request path")

;; ============================================================================
;; Configuration
;; ============================================================================

(defonce config
  (atom {:api-base "/api"
         :auth-header (fn [] {})
         :resource-names {}  ; Optional: {:persons "people"} for custom mappings
         :log-error (fn [_msg _data] nil)
         :get-label (fn [_db _agg-key] nil)  ; (fn [db agg-key] -> "Person")
         :on-http-error nil  ; (fn [status error] -> effect-map or nil) for 401, etc.
         :notify-success nil  ; (fn [title msg] -> effect-map entry) e.g. {:notification {...}}
         :notify-error nil}))

(defn configure!
  "Configure the webapp HTTP/aggregates modules.

   Options:
   - :api-base - Base URL for API requests (default: \"/api\")
   - :auth-header - Function that returns auth header map (default: empty)
   - :resource-names - Map of agg-key to resource name (default: uses agg-key name)
   - :log-error - Function (message data) for error logging
   - :get-label - Function (db agg-key) returning human-readable label
   - :on-http-error - Function (status error) returning effect-map for HTTP errors (e.g. 401)
   - :notify-success - Function (title message) returning effect-map entry for success toast
   - :notify-error - Function (title message) returning effect-map entry for error toast"
  [opts]
  (swap! config merge opts))

(defn get-api-base
  "Get configured API base URL."
  []
  (:api-base @config))

(defn get-auth-header
  "Get auth header map from configured auth-header function."
  []
  ((:auth-header @config)))

(defn handle-http-error
  "Handle HTTP-level errors (e.g. 401). Returns effect map or nil.

   Shared by the re-frame path (aggregates.events/::http-failure) and the
   imperative path (api.http/request!), so the configured 401 -> logout policy
   fires regardless of which HTTP idiom made the call."
  [error]
  (when-let [f (:on-http-error @config)]
    (f (:status error) error)))

;; ============================================================================
;; Request base — pure (no ajax). Single source of truth for uri + headers.
;; ============================================================================

(defn base-opts
  "Pure shared request bits: composed URI (api-base + path) and auth headers.
   Callers layer the ajax JSON request/response formats on top (build-request,
   request!). Path is appended verbatim — the caller controls the leading slash."
  [path]
  {:uri (str (:api-base @config) path)
   :headers ((:auth-header @config))})
